import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getClientDetails, getClientDetailsByUUID } from "../service/client.service";
import { cacheClients, selectClientById } from "../slices/clientSlice";
import type { TClientForm } from "../types/index.type";

type ResolveArgs = {
  id?: number | null;
  uuid?: string | null;
  name?: string | null; // embedded fallback, e.g. note.client_name
};

type ResolvedClient = {
  uuid: string | undefined;
  name: string | undefined;
  client: TClientForm | undefined;
};

/**
 * Resolve a client for display/navigation from whatever a consumer has on hand:
 *   byId cache  ->  embedded values (note/recording payload)  ->  fetch-by-uuid, else fetch-by-id (cached for everyone).
 *
 * No dependency on the full in-memory list, so an off-page client always resolves to the right name +
 * uuid (kills the blank-label / navigate("/clients/undefined") bug class). Consumers that only have a
 * numeric id (e.g. the Superdoc picker) still resolve, because we fall back to GET /client/<id>/ when
 * no uuid is on hand. On a 404 / soft-delete the fetch is swallowed and the consumer falls back to the
 * embedded name.
 */
export const useResolveClient = ({ id, uuid, name }: ResolveArgs): ResolvedClient => {
  const dispatch = useDispatch();
  const cached = useSelector(selectClientById(id));

  useEffect(() => {
    if (cached) return;
    // Prefer uuid (share-aware, and what most consumers carry); fall back to the numeric id so an
    // id-only consumer (e.g. the Superdoc picker) still resolves an off-page client's name after a
    // refresh. Both endpoints are share-aware, so shared clients resolve too. The id != null guard
    // keeps an empty/unselected mount from hitting GET /client/undefined/.
    const request = uuid ? getClientDetailsByUUID(uuid) : id != null ? getClientDetails(id) : null;
    if (!request) return;
    let active = true;
    request
      .then((res) => {
        if (active && res?.data) dispatch(cacheClients([res.data]));
      })
      .catch(() => {
        // 404 / deleted / unshared: leave uncached; the embedded name is the fallback.
      });
    return () => {
      active = false;
    };
  }, [cached, uuid, id, dispatch]);

  return {
    uuid: cached?.uuid ?? uuid ?? undefined,
    name: cached?.name ?? name ?? undefined,
    client: cached,
  };
};

export default useResolveClient;
